// The driver manifests (GET /integrator/drivers), cached per session and shared by
// every setup screen: the integrator detail renders the connection from them, the
// config detail renders its role's declared settings, the jobs setup reads which
// jobs a driver declares.
class DriverManifests
{
    private static _manifests: any[] = null;

    static load(completion: any) {
        if (DriverManifests._manifests != null) { completion(DriverManifests._manifests); return; }
        let ws = MUIWebApplication.sharedInstance().delegate.webService;
        ws.integratorDrivers((drivers: any[], error: any) => {
            // Cache only a SUCCESSFUL answer. A failed fetch used to poison the session
            // with an empty list, and every driver-rendered screen stayed dark until a
            // full page reload — retrying on the next screen is the right behaviour.
            if (error == null && drivers != null && drivers.length > 0) {
                DriverManifests._manifests = drivers;
            }
            completion(DriverManifests._manifests ?? drivers ?? []);
        });
    }

    static find(type: string): any {
        return (DriverManifests._manifests || []).find((d: any) => d["type"] == type) ?? null;
    }

    // The settings the driver declares for a config's role — [] when the driver, the
    // role or the job is unknown. Kind "attribute" entries are model columns the screen
    // already edits natively and are not returned as extra rows.
    static jobSettings(type: string, role: string): any[] {
        let manifest = DriverManifests.find(type);
        if (manifest == null || role == null) return [];
        let job = (manifest["jobs"] ?? []).find((j: any) => j["role"] == role);
        if (job == null) return [];
        return (job["settings"] ?? []).filter((s: any) => s["kind"] != "attribute");
    }
}


// One-line summary of a health record, worst problem first — the integrator list's
// status chip and the detail screen's strip share the wording.
class IntegratorHealthSummary
{
    static text(health: any): string {
        if (health == null) return "";
        let jobs: any[] = health["jobs"] ?? [];

        let halted = jobs.find((j: any) => j["halted"] == true);
        if (halted != null) return MIOLocalizeString("HALTED", "HALTED") + " — " + (halted["blockedOn"] ?? "?");

        if (health["connection"] != null && health["connection"]["ok"] != true) return MIOLocalizeString("NO_CONNECTION", "NO CONNECTION");

        let mapping = health["mapping"] ?? {};
        if (mapping["state"] == "missing") return MIOLocalizeString("NO_MAPPING", "NO MAPPING");
        if (mapping["state"] == "error") return MIOLocalizeString("MAPPING_ERROR", "MAPPING ERROR");

        let missingJob = jobs.find((j: any) => j["exists"] != true);
        if (missingJob != null) return MIOLocalizeString("MISSING_JOB", "MISSING JOB") + ": " + missingJob["role"];

        // The job exists but watches nothing: its source entity is the venue's choice
        // and the config's field is still empty — it would sit silently "ready" forever.
        let missingSource = jobs.find((j: any) => j["missingSourceEntity"] == true);
        if (missingSource != null) return MIOLocalizeString("NO_SOURCE_ENTITY", "NO SOURCE ENTITY") + ": " + missingSource["role"];

        let errors = jobs.reduce((sum: number, j: any) => sum + (j["errors"] ?? 0), 0);
        if (errors > 0) return errors + " " + MIOLocalizeString("SYNC_ERRORS", "SYNC ERRORS");

        return MIOLocalizeString("READY", "READY");
    }
}
